/**
 * SessionCache saves chat context & messages
 */
class SessionCache {
    constructor() {
        this.tenantRel = this._getDefaultTenantRel();

        // Hold tenant-relative data form
        this.ttl = 60 * 60 * 2;
        this.nonPersistenceHistory = [];
    }

    /**
     * @return {string|null}
     */
    getTenantRel() {
        return this.tenantRel;
    }

    /**
     * Get shard instance id
     *
     * @return {string|null}
     */
    getSessionId() {
        return this._get('sessionId');
    }

    /**
     * Set shard instance id
     *
     * @param {string} id
     */
    setSessionId(id) {
        this._set('sessionId', id);
    }

    /**
     * Checks & adds an history item to session cache
     *
     * @param historyItem
     * @return {[]}
     */
    integrateHistoryAddition(historyItem) {
        const updatedHistory = this._validateHistoryItemInCurrent(
            historyItem,
            this.getHistory()
        )

        this.setHistory(updatedHistory);
        return updatedHistory;
    }

    /**
     * Verify external history with local one
     *
     * @param {array} history
     */
    verifyHistory(history) {
        let currentHistory = this.getHistory();

        for (let i = 0; i < history.length; i++) {
            currentHistory = this._validateHistoryItemInCurrent(history[i], currentHistory);
        }

        this.setHistory(currentHistory);
    }

    /**
     * Get history
     *
     * @return {Object}
     */
    getHistory() {
        // If session id isnt set, persistence is disabled
        if (this.getSessionId() === null) {
            return this.nonPersistenceHistory;
        }

        const history = this._get('history');
        if (history === null) {
            return [];
        } else {
            return JSON.parse(history)
        }
    }

    /**
     * Set history
     *
     * @param {Object} historyObject
     */
    setHistory(historyObject) {
        // If session id isnt set, persistence is disabled
        if (this.getSessionId() === null) {
            this.nonPersistenceHistory = historyObject;
            return;
        }

        this._set('history', JSON.stringify(historyObject));
    }

    /**
     * Get shard id
     *
     * @return {null|*}
     */
    getShard() {
        return this._get('shardId');
    }

    /**
     * Set shard id
     *
     * @param {string} id
     *
     * @return {null|*}
     */
    setShard(id) {
        this._set('shardId', id);
    }

    /**
     * Clear current context
     */
    clear() {
        // Also purge non-persistence history
        this.nonPersistenceHistory = [];

        if (this.tenantRel === null) {
            return;
        }

        localStorage.removeItem(this.tenantRel);
    }

    /**
     * Checks validity of "historyItem" & adds it to "currentHistory" if missing
     *
     * @param {object} historyItem
     * @param {array} currentHistory
     *
     * @return {array}
     * @private
     */
    _validateHistoryItemInCurrent(historyItem, currentHistory) {
        // Check parameter for validity
        if (
            typeof historyItem.id === 'undefined' ||
            typeof historyItem.msg === 'undefined' ||
            typeof historyItem.senderType === 'undefined'
        ) {
            return currentHistory;
        }

        for (let i = 0; i < currentHistory.length; i++) {
            if (currentHistory[i].id === historyItem.id) {
                return currentHistory;
            }
        }

        currentHistory.push(historyItem);
        return currentHistory;
    }

    /**
     * @param dataKey
     *
     * @returns {null|*}
     * @private
     */
    _get(dataKey) {
        if (this.tenantRel === null) {
            return null;
        }

        const data = localStorage.getItem(this.tenantRel);
        if (!data) {
            return null;
        }

        const item = JSON.parse(data);

        if (Date.now() > item.ttl) {
            localStorage.removeItem(this.tenantRel);
            return null;
        }

        if (typeof dataKey !== 'undefined') {
            return (typeof item.value[dataKey] !== 'undefined') ? item.value[dataKey] : null;
        }

        return item.value;
    }

    /**
     * @param {string} dataKey
     * @param {string} data
     *
     * @private
     */
    _set(dataKey, data) {
        if (this.tenantRel === null) {
            return;
        }

        let currentVal = this._get();
        if (currentVal === null) {
            currentVal = {};
        }

        currentVal[dataKey] = data;

        const saveValue = JSON.stringify({
            value: currentVal,
            ttl: Date.now() + (this.ttl * 1000),
        })

        localStorage.setItem(this.tenantRel, saveValue);
    }

    /**
     * @return {string|null}
     * @private
     */
    _getDefaultTenantRel() {
        if (typeof document !== 'undefined' && document.referrer !== "") {
            return (new URL(document.referrer)).hostname;
        }

        return null;
    }
}

const sessionCache = new SessionCache();
export default sessionCache;